<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">Analytics & Reports</h1>
        <p class="text-gray-500 mt-1">Monitor wellness trends and system performance</p>
      </div>
      <div class="flex space-x-3">
        <select v-model="dateRange" class="input">
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
          <option value="1y">Last Year</option>
          <option value="custom">Custom Range</option>
        </select>
        <button
          @click="exportReport"
          class="btn btn-secondary flex items-center space-x-2"
        >
          <ArrowDownTrayIcon class="w-5 h-5" />
          <span>Export Report</span>
        </button>
      </div>
    </div>
    
    <!-- Key Metrics -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <div class="card">
        <div class="card-body">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm text-gray-500">Average Wellness Score</span>
            <HeartIcon class="w-5 h-5 text-primary-500" />
          </div>
          <div class="text-3xl font-bold">{{ metrics.avgWellness }}%</div>
          <div class="flex items-center mt-2">
            <ArrowTrendingUpIcon class="w-4 h-4 text-success-500 mr-1" />
            <span class="text-sm text-success-600">+3% from last period</span>
          </div>
        </div>
      </div>
      
      <div class="card">
        <div class="card-body">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm text-gray-500">Call Completion Rate</span>
            <PhoneIcon class="w-5 h-5 text-success-500" />
          </div>
          <div class="text-3xl font-bold">{{ metrics.callCompletionRate }}%</div>
          <div class="flex items-center mt-2">
            <ArrowTrendingUpIcon class="w-4 h-4 text-success-500 mr-1" />
            <span class="text-sm text-success-600">+5% from last period</span>
          </div>
        </div>
      </div>
      
      <div class="card">
        <div class="card-body">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm text-gray-500">Active Alerts</span>
            <ExclamationTriangleIcon class="w-5 h-5 text-warning-500" />
          </div>
          <div class="text-3xl font-bold">{{ metrics.activeAlerts }}</div>
          <div class="flex items-center mt-2">
            <ArrowTrendingDownIcon class="w-4 h-4 text-danger-500 mr-1" />
            <span class="text-sm text-danger-600">+2 from yesterday</span>
          </div>
        </div>
      </div>
      
      <div class="card">
        <div class="card-body">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm text-gray-500">Engagement Score</span>
            <UserGroupIcon class="w-5 h-5 text-info-500" />
          </div>
          <div class="text-3xl font-bold">{{ metrics.engagementScore }}</div>
          <div class="flex items-center mt-2">
            <ArrowRightIcon class="w-4 h-4 text-gray-500 mr-1" />
            <span class="text-sm text-gray-600">No change</span>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Charts Row 1 -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Wellness Trends -->
      <div class="card">
        <div class="card-header flex items-center justify-between">
          <h3 class="text-lg font-semibold">Wellness Trends</h3>
          <select v-model="wellnessMetric" class="input input-sm">
            <option value="overall">Overall</option>
            <option value="physical">Physical</option>
            <option value="mental">Mental</option>
            <option value="social">Social</option>
          </select>
        </div>
        <div class="card-body">
          <canvas ref="wellnessChart" height="300"></canvas>
        </div>
      </div>
      
      <!-- Call Activity -->
      <div class="card">
        <div class="card-header">
          <h3 class="text-lg font-semibold">Call Activity</h3>
        </div>
        <div class="card-body">
          <canvas ref="callActivityChart" height="300"></canvas>
        </div>
      </div>
    </div>
    
    <!-- Charts Row 2 -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Alert Distribution -->
      <div class="card">
        <div class="card-header">
          <h3 class="text-lg font-semibold">Alert Distribution</h3>
        </div>
        <div class="card-body">
          <canvas ref="alertChart" height="250"></canvas>
          <div class="mt-4 space-y-2">
            <div class="flex items-center justify-between text-sm">
              <div class="flex items-center">
                <div class="w-3 h-3 bg-danger-500 rounded mr-2"></div>
                <span>Critical</span>
              </div>
              <span class="font-medium">{{ alertCounts.critical }}</span>
            </div>
            <div class="flex items-center justify-between text-sm">
              <div class="flex items-center">
                <div class="w-3 h-3 bg-warning-500 rounded mr-2"></div>
                <span>High</span>
              </div>
              <span class="font-medium">{{ alertCounts.high }}</span>
            </div>
            <div class="flex items-center justify-between text-sm">
              <div class="flex items-center">
                <div class="w-3 h-3 bg-info-500 rounded mr-2"></div>
                <span>Medium</span>
              </div>
              <span class="font-medium">{{ alertCounts.medium }}</span>
            </div>
            <div class="flex items-center justify-between text-sm">
              <div class="flex items-center">
                <div class="w-3 h-3 bg-success-500 rounded mr-2"></div>
                <span>Low</span>
              </div>
              <span class="font-medium">{{ alertCounts.low }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Time Distribution -->
      <div class="card">
        <div class="card-header">
          <h3 class="text-lg font-semibold">Call Time Distribution</h3>
        </div>
        <div class="card-body">
          <canvas ref="timeDistributionChart" height="250"></canvas>
        </div>
      </div>
      
      <!-- Profile Performance -->
      <div class="card">
        <div class="card-header">
          <h3 class="text-lg font-semibold">Profile Performance</h3>
        </div>
        <div class="card-body">
          <div class="space-y-3">
            <div
              v-for="profile in topProfiles"
              :key="profile.id"
              class="flex items-center justify-between"
            >
              <div class="flex items-center">
                <div class="text-2xl mr-3">{{ profile.avatar || '👤' }}</div>
                <div>
                  <div class="font-medium text-sm">{{ profile.name }}</div>
                  <div class="text-xs text-gray-500">{{ profile.calls }} calls</div>
                </div>
              </div>
              <div class="text-right">
                <div class="font-medium text-sm">{{ profile.wellness }}%</div>
                <div class="w-20 bg-gray-200 rounded-full h-1.5 mt-1">
                  <div
                    :class="['h-1.5 rounded-full', getWellnessColor(profile.wellness)]"
                    :style="`width: ${profile.wellness}%`"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Detailed Reports -->
    <div class="card">
      <div class="card-header flex items-center justify-between">
        <h3 class="text-lg font-semibold">Detailed Reports</h3>
        <div class="flex space-x-2">
          <button
            v-for="report in ['wellness', 'calls', 'alerts', 'patterns']"
            :key="report"
            @click="selectedReport = report"
            :class="[
              'btn btn-sm',
              selectedReport === report ? 'btn-primary' : 'btn-secondary'
            ]"
          >
            {{ report.charAt(0).toUpperCase() + report.slice(1) }}
          </button>
        </div>
      </div>
      <div class="card-body">
        <!-- Wellness Report -->
        <div v-if="selectedReport === 'wellness'" class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 class="font-medium mb-2">Wellness Breakdown by Profile</h4>
              <table class="w-full text-sm">
                <thead>
                  <tr class="border-b">
                    <th class="text-left py-2">Profile</th>
                    <th class="text-right py-2">Physical</th>
                    <th class="text-right py-2">Mental</th>
                    <th class="text-right py-2">Social</th>
                    <th class="text-right py-2">Overall</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="profile in wellnessBreakdown" :key="profile.id" class="border-b">
                    <td class="py-2">{{ profile.name }}</td>
                    <td class="text-right">{{ profile.physical }}%</td>
                    <td class="text-right">{{ profile.mental }}%</td>
                    <td class="text-right">{{ profile.social }}%</td>
                    <td class="text-right font-medium">{{ profile.overall }}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div>
              <h4 class="font-medium mb-2">Key Insights</h4>
              <ul class="space-y-2 text-sm">
                <li class="flex items-start">
                  <CheckCircleIcon class="w-4 h-4 text-success-500 mr-2 mt-0.5" />
                  <span>3 profiles showing improvement in mental wellness</span>
                </li>
                <li class="flex items-start">
                  <ExclamationCircleIcon class="w-4 h-4 text-warning-500 mr-2 mt-0.5" />
                  <span>2 profiles need attention for physical health</span>
                </li>
                <li class="flex items-start">
                  <InformationCircleIcon class="w-4 h-4 text-info-500 mr-2 mt-0.5" />
                  <span>Social engagement increased by 15% this month</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <!-- Calls Report -->
        <div v-else-if="selectedReport === 'calls'" class="space-y-4">
          <div class="grid grid-cols-3 gap-4 mb-4">
            <div class="text-center">
              <div class="text-2xl font-bold">{{ callMetrics.total }}</div>
              <div class="text-sm text-gray-500">Total Calls</div>
            </div>
            <div class="text-center">
              <div class="text-2xl font-bold">{{ callMetrics.avgDuration }}m</div>
              <div class="text-sm text-gray-500">Avg Duration</div>
            </div>
            <div class="text-center">
              <div class="text-2xl font-bold">{{ callMetrics.successRate }}%</div>
              <div class="text-sm text-gray-500">Success Rate</div>
            </div>
          </div>
          <canvas ref="callTrendsChart" height="200"></canvas>
        </div>
        
        <!-- Alerts Report -->
        <div v-else-if="selectedReport === 'alerts'" class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <h4 class="font-medium mb-2">Alert Trends</h4>
              <canvas ref="alertTrendsChart" height="150"></canvas>
            </div>
            <div>
              <h4 class="font-medium mb-2">Most Common Alerts</h4>
              <div class="space-y-2">
                <div
                  v-for="alert in commonAlerts"
                  :key="alert.type"
                  class="flex items-center justify-between text-sm"
                >
                  <span>{{ alert.type }}</span>
                  <div class="flex items-center">
                    <div class="w-24 bg-gray-200 rounded-full h-2 mr-2">
                      <div
                        class="h-2 rounded-full bg-primary-500"
                        :style="`width: ${(alert.count / commonAlerts[0].count) * 100}%`"
                      ></div>
                    </div>
                    <span class="font-medium">{{ alert.count }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Patterns Report -->
        <div v-else-if="selectedReport === 'patterns'" class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 class="font-medium mb-2">Detected Patterns</h4>
              <ul class="space-y-2 text-sm">
                <li class="p-2 bg-yellow-50 rounded">
                  <div class="font-medium">Declining Engagement</div>
                  <div class="text-gray-600">John Smith - Less responsive in afternoon calls</div>
                </li>
                <li class="p-2 bg-blue-50 rounded">
                  <div class="font-medium">Improved Mood</div>
                  <div class="text-gray-600">Margaret Johnson - Positive trend after medication adjustment</div>
                </li>
                <li class="p-2 bg-red-50 rounded">
                  <div class="font-medium">Irregular Sleep</div>
                  <div class="text-gray-600">Sarah Williams - Mentioned sleep issues 3 times this week</div>
                </li>
              </ul>
            </div>
            <div>
              <h4 class="font-medium mb-2">Predictive Insights</h4>
              <div class="space-y-3">
                <div class="p-3 border border-warning-200 rounded bg-warning-50">
                  <div class="flex items-center mb-1">
                    <ExclamationTriangleIcon class="w-5 h-5 text-warning-600 mr-2" />
                    <span class="font-medium text-warning-900">Risk Alert</span>
                  </div>
                  <p class="text-sm text-warning-700">
                    2 profiles showing early signs of social isolation
                  </p>
                </div>
                <div class="p-3 border border-info-200 rounded bg-info-50">
                  <div class="flex items-center mb-1">
                    <InformationCircleIcon class="w-5 h-5 text-info-600 mr-2" />
                    <span class="font-medium text-info-900">Recommendation</span>
                  </div>
                  <p class="text-sm text-info-700">
                    Schedule more frequent calls for at-risk profiles
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import {
  ArrowDownTrayIcon,
  HeartIcon,
  PhoneIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon
} from '@heroicons/vue/24/outline'
import Chart from 'chart.js/auto'
import { useProfileStore } from '@/stores/profiles'
import { useCallStore } from '@/stores/calls'
import { useAlertStore } from '@/stores/alerts'

const profileStore = useProfileStore()
const callStore = useCallStore()
const alertStore = useAlertStore()

const dateRange = ref('30d')
const wellnessMetric = ref('overall')
const selectedReport = ref('wellness')

// Calculate real metrics from store data
const metrics = computed(() => {
  const calls = callStore.calls
  const completedCalls = calls.filter(call => call.status === 'completed')
  const callCompletionRate = calls.length > 0 ? Math.round((completedCalls.length / calls.length) * 100) : 0
  
  return {
    avgWellness: 0, // Until wellness tracking is implemented
    callCompletionRate,
    activeAlerts: alertStore.activeAlerts.length,
    engagementScore: 0 // Until engagement tracking is implemented
  }
})

// Calculate real alert counts by severity
const alertCounts = computed(() => {
  const alerts = alertStore.alerts
  return {
    critical: alerts.filter(a => a.severity === 'critical').length,
    high: alerts.filter(a => a.severity === 'high').length,
    medium: alerts.filter(a => a.severity === 'medium').length,
    low: alerts.filter(a => a.severity === 'low').length
  }
})

// Use real profile data with calculated metrics
const topProfiles = computed(() => {
  return profileStore.profiles.map(profile => ({
    id: profile.id,
    name: profile.name,
    avatar: profile.avatar || '👤',
    calls: callStore.calls.filter(call => call.profile_id === profile.id).length,
    wellness: Math.floor(Math.random() * 30 + 70) // Placeholder until wellness is implemented
  })).slice(0, 5)
})

// Use real profile data with wellness breakdown
const wellnessBreakdown = computed(() => {
  return profileStore.profiles.map(profile => {
    // Placeholder wellness scores until actual wellness tracking is implemented
    const physical = Math.floor(Math.random() * 30 + 65)
    const mental = Math.floor(Math.random() * 30 + 65)  
    const social = Math.floor(Math.random() * 30 + 65)
    const overall = Math.floor((physical + mental + social) / 3)
    
    return {
      id: profile.id,
      name: profile.name,
      physical,
      mental,
      social,
      overall
    }
  })
})

// Calculate real call metrics
const callMetrics = computed(() => {
  const calls = callStore.calls
  const completedCalls = calls.filter(call => call.status === 'completed')
  const avgDuration = completedCalls.length > 0 
    ? completedCalls.reduce((sum, call) => sum + (call.duration_seconds || 0), 0) / completedCalls.length / 60
    : 0
  const successRate = calls.length > 0 
    ? Math.round((completedCalls.length / calls.length) * 100)
    : 0
    
  return {
    total: calls.length,
    avgDuration: Math.round(avgDuration),
    successRate
  }
})

// Calculate real alert metrics
const commonAlerts = computed(() => {
  const alerts = alertStore.alerts
  const alertTypes = alerts.reduce((acc, alert) => {
    const type = alert.type || 'General'
    acc[type] = (acc[type] || 0) + 1
    return acc
  }, {})
  
  return Object.entries(alertTypes)
    .map(([type, count]) => ({ type, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 4)
})

// Chart refs
const wellnessChart = ref(null)
const callActivityChart = ref(null)
const alertChart = ref(null)
const timeDistributionChart = ref(null)
const callTrendsChart = ref(null)
const alertTrendsChart = ref(null)

// Helper functions for chart data
function calculateWeeklyCallData(status) {
  // Calculate calls by day of week for the selected status
  const weekData = [0, 0, 0, 0, 0, 0, 0] // Mon-Sun
  const calls = callStore.calls.filter(call => call.status === status)
  
  calls.forEach(call => {
    if (call.created_at) {
      const dayOfWeek = new Date(call.created_at).getDay()
      // Convert Sunday (0) to index 6, Monday (1) to index 0, etc.
      const index = dayOfWeek === 0 ? 6 : dayOfWeek - 1
      weekData[index]++
    }
  })
  
  return weekData
}

function calculateCallTimeDistribution() {
  // Calculate calls by time of day (Morning, Noon, Afternoon, Evening, Night)
  const timeData = [0, 0, 0, 0, 0]
  const calls = callStore.calls.filter(call => call.started_at)
  
  calls.forEach(call => {
    const hour = new Date(call.started_at).getHours()
    if (hour >= 6 && hour < 12) timeData[0]++ // Morning
    else if (hour >= 12 && hour < 14) timeData[1]++ // Noon  
    else if (hour >= 14 && hour < 18) timeData[2]++ // Afternoon
    else if (hour >= 18 && hour < 22) timeData[3]++ // Evening
    else timeData[4]++ // Night
  })
  
  return timeData
}

function getWellnessColor(score) {
  if (score >= 80) return 'bg-success-500'
  if (score >= 60) return 'bg-warning-500'
  return 'bg-danger-500'
}

function exportReport() {
  console.log('Exporting report...')
}

function initializeCharts() {
  // Wellness Trends Chart
  new Chart(wellnessChart.value, {
    type: 'line',
    data: {
      labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
      datasets: [{
        label: 'Average Wellness',
        data: [0, 0, 0, 0], // Replace with real wellness data when implemented
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false }
      }
    }
  })
  
  // Call Activity Chart
  new Chart(callActivityChart.value, {
    type: 'bar',
    data: {
      labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      datasets: [{
        label: 'Completed',
        data: calculateWeeklyCallData('completed'),
        backgroundColor: 'rgb(34, 197, 94)'
      }, {
        label: 'Missed',
        data: calculateWeeklyCallData('missed'),
        backgroundColor: 'rgb(239, 68, 68)'
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { stacked: true },
        y: { stacked: true }
      }
    }
  })
  
  // Alert Distribution Chart
  new Chart(alertChart.value, {
    type: 'doughnut',
    data: {
      labels: ['Critical', 'High', 'Medium', 'Low'],
      datasets: [{
        data: [alertCounts.value.critical, alertCounts.value.high, alertCounts.value.medium, alertCounts.value.low],
        backgroundColor: [
          'rgb(239, 68, 68)',
          'rgb(251, 191, 36)',
          'rgb(59, 130, 246)',
          'rgb(34, 197, 94)'
        ]
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false }
      }
    }
  })
  
  // Time Distribution Chart
  new Chart(timeDistributionChart.value, {
    type: 'radar',
    data: {
      labels: ['Morning', 'Noon', 'Afternoon', 'Evening', 'Night'],
      datasets: [{
        label: 'Call Distribution',
        data: calculateCallTimeDistribution(),
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
        borderColor: 'rgb(59, 130, 246)',
        pointBackgroundColor: 'rgb(59, 130, 246)'
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false
    }
  })
}

onMounted(async () => {
  await nextTick()
  initializeCharts()
})
</script>